<template>
  <div class="min-h-screen w-full bg-surface-50 dark:bg-surface-900 relative overflow-hidden">
    <!-- Theme Switcher -->
    <div class="absolute top-6 right-6 z-50">
      <ToggleTheme :size="'lg'" />
    </div>

    <!-- Background Pattern -->
    <div class="absolute inset-0">
      <!-- Grid pattern -->
      <div class="absolute inset-0 bg-[linear-gradient(var(--momap)_1px,transparent_1px),linear-gradient(to_right,var(--momap)_1px,transparent_1px)] 
                  bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,black_20%,transparent_100%)] 
                  opacity-[0.03]"></div>
    </div>

    <!-- Login Container -->
    <div class="relative min-h-screen flex items-center justify-center p-6">
      <div class="w-full max-w-md">
        <!-- Logo Section -->
        <div class="text-center mb-12">
          <img src="//cdn.momap.it/branding/logos/logo_red.svg" 
               alt="MoMap" 
               class="w-40 mx-auto hover:scale-105 transition-transform duration-500" />
        </div>

        <!-- Login Form Card -->
        <div class="relative">
          <!-- Glass card -->
          <div class="bg-white dark:bg-surface-800 p-6 lg:p-8 border-l-4 border-momap shadow-lg">
            <!-- Card Header -->
            <div class="flex items-center gap-4 mb-8 border-b border-surface-200 dark:border-surface-700 pb-4">
              <span class="material-symbols-outlined text-3xl text-momap">
                admin_panel_settings
              </span>
              <div>
                <h2 class="text-xl font-bold text-surface-800 dark:text-surface-100">
                  Area Riservata
                </h2>
                <p class="text-surface-500 dark:text-surface-400 text-sm mt-0.5">
                  Accedi alla tua area personale
                </p>
              </div>
            </div>

            <!-- Form -->
            <form @submit.prevent="onSubmit" class="space-y-6">
              <div class="form-group">
                <label class="relative block">
                  <span class="text-sm font-medium text-surface-600 dark:text-surface-400 mb-1.5 block">
                    Username
                  </span>
                  <div class="relative group">
                    <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 
                               text-surface-600 dark:text-surface-400 
                               group-hover:!text-momap transition-colors duration-300">
                      person
                    </span>
                    <InputText 
                      v-model="formData.username" 
                      type="text"
                      class="w-full !pl-11 !py-2.5 !pr-3 transition-colors duration-300
                             bg-surface-100/50 dark:bg-surface-900/50
                             !border-0 !outline-none group-hover:bg-surface-200/50
                             dark:group-hover:bg-surface-800/50"
                      :class="{ 'p-invalid': v$.username.$error }"
                      placeholder="Inserisci il tuo username"
                      @keydown="checkCapsLock"
                    />
                  </div>
                  <Transition name="slide-up">
                    <small class="absolute -bottom-5 left-0 text-red-500 text-xs" v-if="v$.username.$error">
                      {{ v$.username.$errors[0].$message }}
                    </small>
                  </Transition>
                </label>
              </div>

              <!-- Password Field -->
              <div class="form-group">
                <label class="relative block">
                  <span class="text-sm font-medium text-surface-600 dark:text-surface-400 mb-1.5 block">
                    Password
                  </span>
                  <div class="relative group">
                    <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 
                               text-surface-600 dark:text-surface-400 
                               group-hover:!text-momap transition-colors duration-300">
                      lock
                    </span>
                    <Password
                      v-model="formData.password"
                      :feedback="false"
                      toggleMask
                      class="w-full"
                      :class="{ 'p-invalid': v$.password.$error }"
                      placeholder="Inserisci la password"
                      :inputClass="'w-full !pl-11 !py-2.5 !pr-3 transition-colors duration-300 bg-surface-100/50 dark:bg-surface-900/50 !border-0 !outline-none group-hover:bg-surface-200/50 dark:group-hover:bg-surface-800/50'"
                      :pt="{
                        hideIcon: 'material-symbols-outlined text-xl text-surface-600 dark:text-surface-400 group-hover:!text-momap',
                        showIcon: 'material-symbols-outlined text-xl text-surface-600 dark:text-surface-400 group-hover:!text-momap'
                      }"
                      hideIcon="visibility_off"
                      showIcon="visibility"
                      @keydown="checkCapsLock"
                    />
                  </div>
                  <Transition name="slide-up">
                    <small class="absolute -bottom-5 left-0 text-red-500 text-xs" v-if="v$.password.$error">
                      {{ v$.password.$errors[0].$message }}
                    </small>
                  </Transition>
                </label>
              </div>

              <!-- Submit Button -->
              <div class="pt-2">
                <Button 
                  type="submit" 
                  class="w-full relative overflow-hidden group
                         !py-2.5 !text-base font-medium 
                         !bg-green-500 hover:!bg-green-600
                         transition-all duration-300 border-none
                          active:translate-y-0 !border-none dark:!text-white"
                  :loading="loading"
                >
                  <div class="relative flex items-center justify-center gap-2">
                    <!-- Effetto shine migliorato -->
                    <div class="absolute inset-0 bg-white/20 
                                translate-x-[-100%] rotate-[35deg] 
                                group-hover:translate-x-[200%] 
                                transition-transform duration-1000 
                                w-[120px] blur-[8px]">
                    </div>
                    <span class="material-symbols-outlined">login</span>
                    <span>Accedi</span>
                  </div>
                </Button>
              </div>
            </form>

            <!-- Caps Lock Warning -->
            <Transition name="slide-down">
              <div v-if="capsLockOn" 
                   class="absolute -top-16 left-0 right-0 mx-4 
                          bg-yellow-50 dark:bg-yellow-500/20
                          border-l-4 border-yellow-400 p-3 flex items-center gap-3">
                <span class="material-symbols-outlined text-yellow-400">warning</span>
                <span class="text-yellow-700 dark:text-yellow-200 text-sm">Blocco maiuscole attivo</span>
              </div>
            </Transition>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-8 text-center">
          <p class="text-sm text-surface-600/60 dark:text-surface-400/60">
            © {{ new Date().getFullYear() }} MoMap. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from "vue-router";
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import ToggleTheme from '@/components/reusable/ToggleTheme.vue';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();
const loading = ref(false);

const formData = ref({
    username: '',
    password: ''
});

const rules = {
    username: { required, minLength: minLength(3) },
    password: { required, minLength: minLength(6) }
};

const v$ = useVuelidate(rules, formData);

const capsLockOn = ref(false);
const checkCapsLock = (event) => {
    capsLockOn.value = event?.getModifierState && event.getModifierState('CapsLock');
};

const onSubmit = async () => {
    if (loading.value) {
        return;
    }
    
    try {
        loading.value = true;
        const isValid = await v$.value.$validate();
        if (!isValid) {
            loading.value = false;
            return;
        }

        const response = await authStore.login(formData.value);
        
        if (response.success) {
            router.push('/');
        } else {
            console.warn('Login fallito:', response.error);
            toast.add({
                severity: 'error',
                summary: 'Errore',
                detail: response.error || 'Credenziali non valide',
                life: 3000
            });
        }
    } catch (error) {
        console.error('Errore durante il login:', error);
        toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: error.error || 'Errore durante il login',
            life: 3000
        });
    } finally {
        loading.value = false;
    }
};
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active,
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-out;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(10px);
  opacity: 0;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

:deep(.p-password-input) {
  width: 100% !important;
}

:deep(.p-password) {
  width: 100% !important;
}

:deep(.p-inputtext) {
  border-radius: 0 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

:deep(.p-button) {
  border-radius: 0 !important;
}

:deep(.p-password-input),
:deep(.p-inputtext) {
  background: var(--surface-100) !important;
  transition: all 0.3s ease;
}

:deep(.dark .p-password-input),
:deep(.dark .p-inputtext) {
  background: var(--surface-900) !important;
}

/* Stili per l'hover delle icone del password input */
:deep(.p-password .p-icon) {
  color: var(--surface-600);
}

:deep(.dark .p-password .p-icon) {
  color: var(--surface-400);
}

:deep(.group:hover .p-password .p-icon) {
  color: var(--momap) !important;
}

/* Rimuovi gli stili di focus non necessari */
:deep(.p-inputtext:focus),
:deep(.p-password-input:focus) {
  box-shadow: none !important;
  outline: none !important;
  border: none !important;
}

/* Rimuovi i bordi del container password */
:deep(.p-password) {
  border: none !important;
  width: 100% !important;
}

/* Animazione per il pulsante di login */
.before\:skew-x-\[45deg\] {
  transform: skew(45deg);
}

/* Gestione autocomplete */
:deep(.p-inputtext:-webkit-autofill),
:deep(.p-password-input:-webkit-autofill) {
  -webkit-box-shadow: 0 0 0 30px var(--surface-100) inset !important;
  -webkit-text-fill-color: var(--surface-700) !important;
  transition: background-color 5000s ease-in-out 0s;
}

:deep(.dark .p-inputtext:-webkit-autofill),
:deep(.dark .p-password-input:-webkit-autofill) {
  -webkit-box-shadow: 0 0 0 30px var(--surface-900) inset !important;
  -webkit-text-fill-color: var(--surface-200) !important;
}

/* Assicurati che le icone siano sempre sopra l'input */
.material-symbols-outlined {
  z-index: 2;
}

/* Migliora il contrasto delle icone */
:deep(.p-password .p-icon),
.material-symbols-outlined {
  color: var(--surface-600) !important;
  z-index: 2;
}

:deep(.dark .p-password .p-icon),
.dark .material-symbols-outlined {
  color: var(--surface-400) !important;
}

:deep(.group:hover .p-password .p-icon),
.group:hover .material-symbols-outlined {
  color: var(--momap) !important;
}

/* Assicurati che l'input non copra le icone */
:deep(.p-inputtext),
:deep(.p-password-input) {
  background: var(--surface-100) !important;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

:deep(.dark .p-inputtext),
:deep(.dark .p-password-input) {
  background: var(--surface-900) !important;
}
</style>